import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { googleAdsApi } from '@/services/api';
import { useGoogleMaps } from '@/hooks/useGoogleMaps';
import { CITY_MAP_OPTIONS, TONE } from './mapStyle';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import {
  Loader2, AlertTriangle, Wrench, ClipboardCheck, CircleDashed, Hourglass, Home,
} from 'lucide-react';

const num = (v) => Number(v || 0).toLocaleString('en-IN');
const when = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: '2-digit',
  });
};

const Stat = ({ icon: Icon, tone, label, value, sub, testId }) => (
  <div className="rounded-lg border border-slate-700/70 bg-slate-800/40 px-3 py-2" data-testid={testId}>
    <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-slate-400">
      <Icon className="h-3.5 w-3.5" style={tone ? { color: tone } : undefined} />
      {label}
    </div>
    <div className="mt-0.5 text-lg font-semibold text-white">{value}</div>
    {sub && <div className="text-[11px] text-slate-500">{sub}</div>}
  </div>
);

const Chip = ({ on, onClick, tone, icon: Icon, label, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    aria-pressed={on}
    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
      on ? 'border-transparent text-slate-900' : 'border-slate-700 text-slate-400 hover:text-slate-200'
    }`}
    style={on ? { background: tone } : undefined}
  >
    <Icon className="h-3.5 w-3.5" />
    {label}
  </button>
);

export const CityDotsDrawer = ({ city, from, to, onClose }) => {
  const name = city?.city || null;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [layers, setLayers] = useState({ service: true, inspection: true, approximate: true });
  const [drawError, setDrawError] = useState(null);

  const { ready, error: mapsError } = useGoogleMaps(Boolean(name));
  const holder = useRef(null);
  const map = useRef(null);
  const overlays = useRef([]);
  const info = useRef(null);

  useEffect(() => {
    if (!name) { setData(null); return undefined; }
    let alive = true;
    setLoading(true);
    setError(null);
    googleAdsApi.getCityPoints({ city: name, date_from: from, date_to: to })
      .then((res) => { if (alive) setData(res.data); })
      .catch((e) => { if (alive) setError(e?.response?.data?.detail || 'Could not load customer locations'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [name, from, to]);

  // The map instance cannot outlive the drawer: its container is unmounted on close.
  useEffect(() => {
    if (!name) { map.current = null; overlays.current = []; }
  }, [name]);

  const points = useMemo(
    () => (data?.points || []).filter((p) => layers[p.kind]),
    [data, layers],
  );

  const clear = useCallback(() => {
    overlays.current.forEach((o) => o.setMap(null));
    overlays.current = [];
  }, []);

  useEffect(() => {
    if (!name || !ready || !holder.current || !data) return undefined;
    // A Google Maps failure must stay inside this drawer — never reach the page's error
    // boundary, which would blank the whole Services tab behind it.
    try {
      const g = window.google.maps;
      if (!map.current) {
        map.current = new g.Map(holder.current, {
          ...CITY_MAP_OPTIONS,
          center: data.center,
          zoom: 11,
        });
        info.current = new g.InfoWindow();
      }
      clear();

      const bounds = new g.LatLngBounds();
      points.forEach((p) => {
        const position = { lat: p.lat, lng: p.lng };
        bounds.extend(position);
        const color = p.kind === 'inspection' ? TONE.inspection : TONE.service;
        const marker = new g.Marker({
          map: map.current, position, zIndex: p.kind === 'service' ? 3 : 2,
          title: `${p.name} · ${p.kind === 'service' ? 'Service' : 'Inspection'}`,
          icon: {
            path: g.SymbolPath.CIRCLE, scale: 6, fillColor: color, fillOpacity: 0.9,
            strokeColor: '#0f172a', strokeWeight: 1.5,
          },
        });
        marker.addListener('click', () => {
          info.current.setContent(
            `<div style="font-family:ui-sans-serif;min-width:200px;max-width:260px;color:#0f172a">
               <div style="font-weight:700;font-size:13px">${p.name}</div>
               <div style="font-size:11px;color:#475569;margin:1px 0 6px">
                 ${p.kind === 'service' ? 'Service enquiry' : 'Inspection'}
                 ${p.status ? ` · ${p.status}` : ''} ${p.when ? ` · ${when(p.when)}` : ''}
               </div>
               ${p.detail ? `<div style="font-size:11px">${p.detail}</div>` : ''}
               <div style="font-size:11px;color:#475569;margin-top:4px">${p.address || ''}</div>
               ${p.mobile ? `<div style="font-size:11px;color:#64748b;margin-top:4px">•••••• ${p.mobile}</div>` : ''}
               ${p.address_from === 'inspection' && p.kind === 'service'
                 ? '<div style="font-size:10px;color:#0d9488;margin-top:4px">Address taken from this customer\'s inspection</div>' : ''}
             </div>`);
          info.current.setPosition(position);
          info.current.open(map.current);
        });
        overlays.current.push(marker);
      });

      // The ones we cannot place: ONE dashed ring on the city centre carrying the count.
      // Never scattered — a dot that looks real but is invented is worse than an honest gap.
      const approx = data.approximate?.count || 0;
      if (layers.approximate && approx > 0) {
        const centre = data.center;
        overlays.current.push(new g.Circle({
          map: map.current, center: centre, radius: 2600, clickable: false,
          strokeColor: TONE.approximate, strokeOpacity: 0.85, strokeWeight: 2,
          fillColor: TONE.approximate, fillOpacity: 0.08, zIndex: 1,
        }));
        overlays.current.push(new g.Marker({
          map: map.current, position: centre, clickable: false, zIndex: 4,
          icon: { path: g.SymbolPath.CIRCLE, scale: 0, fillOpacity: 0, strokeOpacity: 0 },
          label: { text: `${num(approx)} approximate`, color: '#fbbf24',
                   fontSize: '12px', fontWeight: '700' },
        }));
        bounds.extend(centre);
      }

      if (points.length > 1) {
        map.current.fitBounds(bounds, 48);
        // One wrong address 200 km away would otherwise zoom the whole city out to a
        // coastline with three visible dots, hiding the hundreds sitting on top of each
        // other. Clamped to a city-sized view.
        g.event.addListenerOnce(map.current, 'idle', () => {
          const zoom = map.current.getZoom();
          if (zoom < 9) { map.current.setZoom(11); map.current.setCenter(data.center); }
          else if (zoom > 14) map.current.setZoom(14);
        });
      } else {
        map.current.setCenter(data.center);
      }
      setDrawError(null);
    } catch (e) {
      setDrawError(e.message || 'Could not draw the map');
    }
    return clear;
  }, [name, ready, data, points, layers.approximate, clear]);

  const t = data?.totals || {};
  const approximate = data?.approximate || {};

  return (
    <Sheet open={Boolean(name)} onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent
        side="right"
        data-testid="city-dots-drawer"
        className="w-full sm:max-w-none sm:w-[94vw] lg:w-[1150px] bg-slate-900 border-slate-700 p-0 overflow-y-auto [&>button]:text-slate-400 [&>button]:opacity-100 [&>button:hover]:text-white"
      >
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-slate-800">
          <SheetTitle className="text-white text-lg flex items-center gap-2" data-testid="city-dots-title">
            {name}
            {data && !data.served && (
              <span className="rounded bg-rose-500/15 px-2 py-0.5 text-[11px] font-semibold text-rose-300">
                car service not live here
              </span>
            )}
          </SheetTitle>
          <p className="text-xs text-slate-400">
            Where this city&apos;s customers actually are. A service enquiry sits on its own
            address when it has one, otherwise on the same customer&apos;s inspection address.
          </p>
        </SheetHeader>

        <div className="px-6 py-4">
          {loading && (
            <div className="flex items-center gap-2 text-sm text-slate-400" data-testid="city-dots-loading">
              <Loader2 className="h-4 w-4 animate-spin" /> Locating customers…
            </div>
          )}

          {error && (
            <div className="flex items-start gap-2 text-sm text-rose-300" data-testid="city-dots-error">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" /> {error}
            </div>
          )}

          {data && (
            <>
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                <Stat icon={Home} label="On a real address" value={num(t.precise)}
                      sub="geocoded from the address we hold" testId="city-dots-precise" />
                <Stat icon={CircleDashed} tone={TONE.approximate} label="Approximate"
                      value={num(t.approximate)} sub="no usable address — city centre"
                      testId="city-dots-approximate" />
                <Stat icon={Wrench} tone={TONE.service} label="Service enquiries"
                      value={num(t.service_leads)} testId="city-dots-service-total" />
                <Stat icon={ClipboardCheck} tone={TONE.inspection} label="Inspections"
                      value={num(t.inspections)} testId="city-dots-inspection-total" />
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-2">
                <Chip on={layers.service} tone={TONE.service} icon={Wrench} label="Service"
                      testId="city-dots-layer-service"
                      onClick={() => setLayers((l) => ({ ...l, service: !l.service }))} />
                <Chip on={layers.inspection} tone={TONE.inspection} icon={ClipboardCheck}
                      label="Inspections" testId="city-dots-layer-inspection"
                      onClick={() => setLayers((l) => ({ ...l, inspection: !l.inspection }))} />
                <Chip on={layers.approximate} tone={TONE.approximate} icon={CircleDashed}
                      label="Approximate" testId="city-dots-layer-approximate"
                      onClick={() => setLayers((l) => ({ ...l, approximate: !l.approximate }))} />
                {Boolean(data.pending_geocodes) && (
                  <span className="inline-flex items-center gap-1.5 text-[11px] text-amber-300"
                        data-testid="city-dots-pending">
                    <Hourglass className="h-3.5 w-3.5" />
                    {num(data.pending_geocodes)} addresses still being located — reopen in a moment
                  </span>
                )}
              </div>

              <div className="mt-4 rounded-xl overflow-hidden border border-slate-700 relative">
                <div ref={holder} className="h-[520px] w-full bg-slate-900"
                     data-testid="city-dots-canvas" />
                {!ready && !mapsError && !drawError && (
                  <div className="absolute inset-0 flex items-center justify-center bg-slate-900/80 text-sm text-slate-400">
                    <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading Google Maps…
                  </div>
                )}
                {(mapsError || drawError) && (
                  <div className="absolute inset-0 flex items-center justify-center text-sm text-amber-300"
                       data-testid="city-dots-maps-error">
                    {mapsError || drawError}
                  </div>
                )}
              </div>

              <p className="mt-3 text-[11px] leading-relaxed text-slate-500" data-testid="city-dots-notes">
                {num(approximate.no_address)} had no address at all and{' '}
                {num(approximate.city_level_address)} carried an address that is only
                &ldquo;City, State, India&rdquo; — that geocodes to the city centre, so showing it
                as somebody&apos;s home would be a false precision. Both are counted in the one
                dashed ring instead.
                {Boolean(t.truncated) && ` ${num(t.truncated)} further dots are not drawn to keep the map responsive.`}
              </p>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default CityDotsDrawer;
